import type { ColDef as LegacyColDef } from '@ag-grid-community/core';
import { AgGridReact as AgGridReactV27 } from '@ag-grid-community/react';
import { DocRenderer } from '@cyntler/react-doc-viewer';
import {
  AllCommunityModule,
  type ColDef as ColDefV35,
  ModuleRegistry,
} from 'ag-grid-community';
import { AgGridReact as AgGridReactV35 } from 'ag-grid-react';
import Papa, { ParseResult } from 'papaparse';
import { useEffect, useState } from 'react';

import { portalTheme } from 'src/components/AgGrid/portalTheme';
import { useFeatureFlags } from 'src/context/flagsContext';

// v35's modular build requires the row-model module to be registered before
// any AgGridReact mounts. Idempotent — the wrapper at src/components/AgGrid
// also registers it.
ModuleRegistry.registerModules([AllCommunityModule]);

const tableContainerStyle = {
  height: '100vh',
  width: '100vw',
  marginTop: '60px',
  backgroundColor: 'white',
};

type RowData = Record<string, string | number | boolean>;

export const CsvRenderer: DocRenderer = ({ mainState }) => {
  const { currentDocument } = mainState;
  const { EnableAgGridUpgrade } = useFeatureFlags();

  const [data, setData] = useState<RowData[] | undefined>(undefined);
  const [columns, setColumns] = useState<LegacyColDef[]>([]);

  useEffect(() => {
    const parseData = async () => {
      const fileUrl = currentDocument?.uri || '';

      const blob = await fetch(fileUrl).then((res) => res.blob());
      const fileBlob = new File([blob], currentDocument?.fileName || '');

      Papa.parse(fileBlob, {
        header: true,
        dynamicTyping: true,
        complete: handleDataChange,
      });
    };

    parseData();

    return () => {
      setData(undefined);
      setColumns([]);
    };
  }, [currentDocument?.uri]);

  const makeColumns = (rawColumns: string[]): LegacyColDef[] => {
    return rawColumns.map((column) => {
      return {
        field: column,
        sortable: true,
        minWidth: 150,
        resizable: true,
      };
    });
  };

  const handleDataChange = (file: ParseResult<RowData>) => {
    setColumns(makeColumns(file.meta.fields || []));
    setData(file.data);
  };

  if (EnableAgGridUpgrade) {
    return (
      <>
        <div className="ag-theme-portal" style={tableContainerStyle}>
          <AgGridReactV35
            theme={portalTheme}
            rowHeight={30}
            headerHeight={30}
            rowData={data}
            columnDefs={columns as unknown as ColDefV35[]}
            enableCellTextSelection
            defaultColDef={{ flex: 1, minWidth: 100 }}
          />
        </div>
      </>
    );
  }

  return (
    <div className="ag-theme-portal" style={tableContainerStyle}>
      <AgGridReactV27
        rowHeight={30}
        headerHeight={30}
        rowData={data}
        columnDefs={columns}
        gridOptions={{
          enableCellTextSelection: true,
          defaultColDef: {
            flex: 1,
            minWidth: 100,
          },
        }}
      />
    </div>
  );
};

CsvRenderer.fileTypes = ['text/csv'];
CsvRenderer.weight = 1;
